import React, { useEffect, useState } from 'react'
import TopHeader from './top-header'
import LanguageSwitch from './language-switch'
import NavItem from './nav-item'
import AppLogo from './app-logo'
import ScanIcon from 'icon/scanIcon'
import WebhookIcon from 'icon/webhookicon'
import BroadcastIcon from 'icon/broadcasticon'
import StarIcon from 'icon/staricon'
import CampaignsIcon from 'icon/campaigns'
import GraphIcon from 'icon/graph'
import BasicinfoIcon from 'icon/basicinfo'
import TeamIcon from 'icon/team'
import KeyIcon2 from 'icon/key'
import BillIcon from 'icon/bill'
import { useNavigate, useLocation } from 'react-router-dom'
import { useLocationInfo } from 'context/location'
import { useAuth } from 'context/auth'
import { visibleItems } from './menu-access'
import { useT } from 'utils/i18n'

// CR-21 menu (plans/REDESIGN.md, phase 2). `keys` are the permission / plan keys an item
// stands for; Team also covers Roles, and opens Roles when only that is allowed.
const menu = [
  { label: 'Dashboard', ar: 'لوحة التحكم', path: '/dashboard', icon: <GraphIcon />, keys: ['dashboard'] },
  { label: 'Campaigns', ar: 'الحملات', path: '/campaigns', icon: <CampaignsIcon />, keys: ['campaigns'] },
  { label: 'Customers', ar: 'العملاء', path: '/customers', icon: <StarIcon />, keys: ['customers'] },
  { label: 'Scanner', ar: 'الماسح', path: '/scanner', icon: <ScanIcon />, keys: ['scanner'] },
  { label: 'Broadcast', ar: 'الرسائل الجماعية', path: '/broadcast', icon: <BroadcastIcon />, keys: ['broadcast'] },
  { label: 'Team', ar: 'الفريق', path: '/team', icon: <TeamIcon />, keys: ['team', 'roles'], paths: { team: '/team', roles: '/roles' }, match: ['/roles'] },
]

// Settings; Webhooks moved here from the main menu (CR-21). Items without keys are
// always shown, as before.
const settings = [
  { label: 'General info', ar: 'المعلومات العامة', path: '/settings/generalinfo', icon: <BasicinfoIcon /> },
  { label: 'Billing', ar: 'الفواتير', path: '/settings/billings', icon: <BillIcon /> },
  { label: 'Plans', ar: 'الباقات', path: '/settings/plans', icon: <StarIcon /> },
  { label: 'API key', ar: 'مفتاح API', path: '/settings/apikey', icon: <KeyIcon2 /> },
  // its route checks the role permission only, not plan features (admin-routes)
  { label: 'Webhooks', ar: 'الويب هوك', path: '/webhook', icon: <WebhookIcon />, keys: ['webhook'], plan: false },
]

const inSettings = (pathname) => pathname.includes('settings') || pathname.startsWith('/webhook')

function Sidebar({ openSideMenu, setOpenSideMenu }) {
  const { saasPlan } = useLocationInfo()
  const { currentUser } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()
  const { t } = useT()
  const [showSettings, setShowSettings] = useState(inSettings(location.pathname))

  const scope = { roles: currentUser?.roles, features: saasPlan?.features }
  const items = visibleItems(showSettings ? settings : menu, scope)

  useEffect(() => {
    setShowSettings(inSettings(location.pathname))
    setOpenSideMenu(false) // close the mobile menu on route change
  }, [location.pathname, setOpenSideMenu])

  const settingsIcon = (
    <svg viewBox='0 0 24 24' width='18' height='18' fill='none' stroke='currentColor' strokeWidth='1.7' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true'>
      <path d='M4 7h10M18 7h2M4 17h4M12 17h8' />
      <circle cx='16' cy='7' r='2' />
      <circle cx='10' cy='17' r='2' />
    </svg>
  )
  const backIcon = (
    <svg viewBox='0 0 24 24' width='18' height='18' fill='none' stroke='currentColor' strokeWidth='1.7' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true' className='rtl:-scale-x-100'>
      <path d='M15 6l-6 6 6 6' />
    </svg>
  )

  return (
    <div>
      {/* Top header: the studio shell bar */}
      <nav className='fixed top-0 z-40 inset-x-0 md:start-[260px] h-[68px] bg-surface border-b border-line'>
        <div className='h-full px-4 md:px-7 flex items-center justify-between md:justify-end gap-3'>
          <button
            type='button'
            className='inline-flex items-center justify-center w-10 h-10 rounded-control text-muted hover:bg-action-wash hover:text-ink md:hidden'
            onClick={() => setOpenSideMenu(!openSideMenu)}
          >
            <span className='sr-only'>{t('Open menu', 'افتح القائمة')}</span>
            <svg className='w-6 h-6' aria-hidden='true' fill='currentColor' viewBox='0 0 20 20' xmlns='http://www.w3.org/2000/svg'>
              <path clipRule='evenodd' fillRule='evenodd' d='M2 4.75A.75.75 0 012.75 4h14.5a.75.75 0 010 1.5H2.75A.75.75 0 012 4.75zm0 10.5a.75.75 0 01.75-.75h7.5a.75.75 0 010 1.5h-7.5a.75.75 0 01-.75-.75zM2 10a.75.75 0 01.75-.75h14.5a.75.75 0 010 1.5H2.75A.75.75 0 012 10z' />
            </svg>
          </button>
          <div className='flex items-center gap-3'>
            <LanguageSwitch />
            <TopHeader />
          </div>
        </div>
      </nav>

      {/* Sidebar */}
      <aside
        aria-label={t('Main menu', 'القائمة الرئيسية')}
        className={`fixed inset-y-0 start-0 z-50 w-[260px] h-[100dvh] flex flex-col bg-nav border-e border-line transition-transform duration-300 ease-studio md:translate-x-0 md:rtl:translate-x-0 ${
          openSideMenu ? 'translate-x-0' : '-translate-x-full rtl:translate-x-full'
        }`}
      >
        <div className='h-[68px] shrink-0 flex items-center px-5 border-b border-line'>
          <AppLogo />
        </div>

        <div className='flex-1 overflow-y-auto px-3 py-4'>
          {showSettings && (
            <ul className='mb-3'>
              <NavItem label={t('Back to menu', 'العودة إلى القائمة')} path='/' icon={backIcon} onClick={() => setShowSettings(false)} />
            </ul>
          )}
          <p className='px-3 mb-2 text-[10px] font-bold uppercase tracking-[1.5px] text-muted'>{showSettings ? t('Settings', 'الإعدادات') : t('Menu', 'القائمة')}</p>
          <ul className='space-y-1'>
            {items.map((item) => (
              <NavItem key={item.label} {...item} label={t(item.label, item.ar)} />
            ))}
          </ul>
        </div>

        {!showSettings && (
          <div className='shrink-0 px-3 py-3 border-t border-line'>
            <ul>
              <NavItem label={t('Settings', 'الإعدادات')} path='/settings/generalinfo' icon={settingsIcon} onClick={() => { setShowSettings(true); navigate('/settings/generalinfo') }} />
            </ul>
          </div>
        )}
      </aside>

      {/* Mobile overlay for closing the menu */}
      {openSideMenu && <div className='fixed inset-0 bg-[#17101f60] z-30 md:hidden' onClick={() => setOpenSideMenu(false)} />}
    </div>
  )
}

export default Sidebar
